import { useSyncExternalStore } from "react";

export interface ForecastedSalesCellPopoverTarget {
  /** The clicked cell element — popover anchor and outline host. */
  anchorEl: HTMLElement;
  sku: string;
  /** null = SKU-level (aggregate across all locations). */
  location: string | null;
  /** Epoch ms of the clicked date column (the matrix period start). */
  periodStartMs: number;
  dateHierarchy: "day" | "week" | "month";
  /**
   * The value the grid shows in the clicked cell. Hub walks render forecasted
   * sales as an outflow (negative) while the transform reports magnitudes;
   * the popover uses this to say so rather than silently disagree.
   */
  cellValue?: number | null;
}

// Module-level store so matrix cells can open the popover without threading
// props (or state) through MUIFlatTable / the chunked table builder. Opening
// and closing re-renders ONLY subscribers (the popover itself) — the table's
// props never change, so react-virtuoso repaints zero cells. Same contract as
// po_cell_popover/store and unfulfilled_demand_cell_popover/store.
let currentTarget: ForecastedSalesCellPopoverTarget | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function openForecastedSalesCellPopover(
  target: ForecastedSalesCellPopoverTarget,
): void {
  currentTarget = target;
  emit();
}

export function closeForecastedSalesCellPopover(): void {
  if (currentTarget === null) return;
  currentTarget = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ForecastedSalesCellPopoverTarget | null {
  return currentTarget;
}

export function useForecastedSalesCellPopoverTarget(): ForecastedSalesCellPopoverTarget | null {
  return useSyncExternalStore(subscribe, getSnapshot);
}
